import type {Call,CoachAskAnswer} from '@bcio-voice/contracts';
import {type FormEvent,type KeyboardEvent,useCallback,useEffect,useMemo,useRef,useState,useSyncExternalStore} from 'react';
import {Sparkles} from 'lucide-react';
import {isAbortError} from '../../lib/api.ts';
import {useWorkspace} from '../../state/workspace.tsx';
import {Icon} from '../../ui/Icon.tsx';
import {ErrorPanel} from '../../ui/StatePanel.tsx';
import {ASK_MAX_LENGTH,UNREADABLE_ANSWER,cleanQuestion,escapeClears} from '../../features/transcript/ask.ts';
import {
 COACH_QUICK_ACTIONS,
 COACH_STORAGE_KEY,
 CoachAskClient,
 CoachPoller,
 type CoachQaEntry,
 type CoachState,
 coachAnsweredLine,
 coachAskErrorView,
 coachDefaultEnabled,
 coachGroundingLine,
 coachQaReducer,
 coachStorage,
 coachSwitchCopy,
 type CoachSwitchCopy,
 coachUserKey,
 coachVisible,
 isRepOnlyQuickAction,
 readCoachAskResponse,
 saveCoachPreference,
 subscribeCoachPreference,
} from './coach.ts';

const CSS=`
.call-insights { display:grid; grid-template-columns:repeat(auto-fit,minmax(min(100%,20rem),1fr)); gap:var(--space-3); align-items:start; }
.call-insights > * { min-width:0; }
.coach-panel { border:1px solid var(--rule); border-radius:var(--radius-panel); padding:var(--space-3); background:var(--surface); display:grid; gap:var(--space-2); }
.coach-toggle { display:flex; align-items:center; gap:var(--space-2); min-height:44px; cursor:pointer; font-weight:var(--wght-strong); }
.coach-toggle input { width:20px; height:20px; accent-color:var(--ink); }
.coach-lines { display:grid; gap:var(--space-2); margin:0; padding-inline-start:var(--space-4); }
.coach-lines li { overflow-wrap:anywhere; line-height:var(--leading-read); }
.coach-panel p { margin:0; }
.coach-qa { display:grid; gap:var(--space-2); padding-top:var(--space-2); border-top:1px solid var(--rule); }
.coach-qa-quick { display:flex; flex-wrap:wrap; gap:var(--space-1); }
.coach-qa-form { margin:0; display:grid; gap:var(--space-1); }
.coach-qa-results { display:grid; gap:var(--space-2); margin:0; padding:0; list-style:none; }
.coach-qa-entry { display:grid; gap:var(--space-1); padding:var(--space-2); border:1px solid var(--rule); border-radius:var(--radius-control); background:var(--paper); }
.coach-qa-question { font-weight:var(--wght-strong); overflow-wrap:anywhere; }
.coach-qa-suggestion { line-height:var(--leading-read); overflow-wrap:anywhere; }
.coach-qa-say { padding:var(--space-1) var(--space-2); border-inline-start:3px solid var(--ink); background:var(--surface); line-height:var(--leading-read); overflow-wrap:anywhere; }
.coach-qa-rep-only { padding:var(--space-1) var(--space-2); border-inline-start:3px dashed var(--rule); font-style:italic; }
.coach-qa-pace { margin:0; }
.coach-qa-context { overflow-wrap:anywhere; }
.coach-qa-notes { display:flex; flex-wrap:wrap; gap:var(--space-1) var(--space-2); }
.coach-switch { display:grid; gap:var(--space-1); padding:var(--space-2) 0; border-bottom:1px solid var(--rule); }
.coach-switch-btn { display:flex; align-items:center; gap:var(--space-2); width:100%; min-height:44px; padding:0 var(--space-2); border:1px solid var(--rule); border-radius:var(--radius-control); background:var(--surface); color:inherit; font:inherit; font-weight:var(--wght-strong); text-align:start; cursor:pointer; }
.coach-switch-btn[aria-checked="true"] { border-color:var(--ink); }
.coach-switch-btn:disabled { cursor:default; opacity:.7; }
.coach-switch-btn .chip { margin-inline-start:auto; }
.coach-switch .field-hint { margin:0; }
`;

export interface CoachQaViewProps {
 entries:readonly CoachQaEntry[];
 draft:string;
 onDraft:(text:string)=>void;
 onAsk:(question:string)=>void;
 onClear:()=>void;
 onRetry:(id:string)=>void;
}

export function CoachStyles(){return <style href="sidetone-call-coach" precedence="default">{CSS}</style>;}

function CoachQaSection({callId,qa}:{callId:string;qa:CoachQaViewProps}){
 const busy=qa.entries.some(e=>e.status==='loading');
 const ready=!busy&&cleanQuestion(qa.draft)!=='';
 const inputId=`coach-qa-input-${callId}`;
 const submit=(e:FormEvent)=>{
  e.preventDefault();
  if(ready)qa.onAsk(qa.draft);
 };
 const keys=(e:KeyboardEvent<HTMLInputElement>)=>{
  if(escapeClears(e.key,qa.draft)){
   e.preventDefault();
   qa.onClear();
  }
 };
 return (
  <div className="coach-qa" aria-busy={busy?'true':undefined}>
   <p className="meta coach-qa-pace">One short line first: ask for 20 to 30 seconds, keep to a 2-minute ceiling if you promised it. Spoken lines and rep-only notes stay separate.</p>
   <div className="coach-qa-quick" role="group" aria-label="Quick coaching actions">
    {COACH_QUICK_ACTIONS.map(q=>(
     <button key={q} type="button" className="btn btn-small coach-qa-quick-btn" disabled={busy} onClick={()=>qa.onAsk(q)}>{q}</button>
    ))}
   </div>
   <form className="field coach-qa-form" onSubmit={submit} noValidate>
    <label htmlFor={inputId}>Ask Coach during this call</label>
    <div className="input-with-action">
     <input
      id={inputId}
      type="text"
      maxLength={ASK_MAX_LENGTH}
      autoComplete="off"
      enterKeyHint="send"
      placeholder="Ask how to answer an objection or what to say next…"
      aria-disabled={busy?'true':undefined}
      readOnly={busy}
      value={qa.draft}
      onChange={e=>qa.onDraft(e.target.value)}
      onKeyDown={keys}
     />
     <button type="submit" className="btn btn-primary" disabled={!ready}>Ask Coach</button>
    </div>
    <p className="field-hint">Grounded in this call's live transcript and CRM card. Suggestions are separated from customer statements.</p>
   </form>
   {qa.entries.length?(
    <ol className="coach-qa-results" aria-label="Coaching answers, newest first">
     {qa.entries.map(entry=>(
      <li key={entry.id} className="coach-qa-entry">
       <p className="coach-qa-question"><span className="visually-hidden">Question: </span>{entry.question}</p>
       {entry.status==='loading'?(
        <p className="meta" role="status" aria-busy="true">Coach is preparing a suggestion…</p>
       ):entry.status==='error'?(
        <ErrorPanel error={entry.error} onRetry={()=>qa.onRetry(entry.id)} compact />
       ):(
        <>
         <CoachAnswerLines question={entry.question} answer={entry.answer} />
         {entry.answer.customer_context?(
          <p className="coach-qa-context meta">
           <strong>Customer / CRM context: </strong>{entry.answer.customer_context}
          </p>
         ):null}
         <p className="meta coach-qa-notes">
          <span>{coachAnsweredLine(entry.answer)}</span>
          <span>{coachGroundingLine(entry.answer)}</span>
          {entry.answer.provisional?<span className="tone-warn">Part of the transcript was still provisional</span>:null}
          {entry.answer.truncated?<span>Based on the most recent transcript lines</span>:null}
         </p>
        </>
       )}
      </li>
     ))}
    </ol>
   ):null}
  </div>
 );
}

/** The sayable line comes first; everything meant only for the rep is labelled so it is never read aloud. */
export function CoachAnswerLines({question,answer}:{question:string;answer:CoachAskAnswer}){
 const sayable=answer.state==='answered'&&!isRepOnlyQuickAction(question)?answer.suggested_wording:null;
 if(sayable)return <>
  <p className="coach-qa-say"><strong>Say aloud: </strong>“{sayable}”</p>
  <p className="coach-qa-suggestion"><strong>Coach note (rep only): </strong>{answer.suggestion}</p>
 </>;
 return <>
  <p className="coach-qa-suggestion coach-qa-rep-only"><strong>{answer.state==='no_answer'?'No answer in call context: ':'For you (rep only — do not say aloud): '}</strong>{answer.suggestion}</p>
  {answer.suggested_wording?<p className="coach-qa-suggestion coach-qa-rep-only"><strong>Also for you: </strong>{answer.suggested_wording}</p>:null}
 </>;
}

export function CoachPanelView({callId,enabled,onToggle,state,qa}:{callId:string;enabled:boolean;onToggle:(on:boolean)=>void;state:CoachState;qa?:CoachQaViewProps}){
 const id='coach-'+callId;
 return <section className="coach-panel" aria-labelledby={id}>
  <CoachStyles />
  <label className="coach-toggle" id={id}><input type="checkbox" checked={enabled} onChange={e=>onToggle(e.target.checked)} />Automatic Coach tips</label>
  {enabled?<div aria-busy={state.thinking}>
   {state.thinking?<p className="meta" role="status">Coach is thinking</p>:null}
   {state.error?<p className="meta" role="status">{state.error}</p>:null}
   {state.result?.stale&&state.result.lines.length>0&&!state.error?<p className="meta" role="status">Updating from new speech…</p>:null}
   <div aria-live="polite" aria-atomic="true">
   {state.result?.lines.length?<ul className="coach-lines">{state.result.lines.map(line=><li key={line}>{line}</li>)}</ul>:
    !state.thinking&&!state.error?<p className="meta">{state.result?.stale?'Waiting for more final transcript.':'Nothing to add.'}</p>:null}
   </div>
  </div>:<p className="meta">Turn on for automatic tips from this call's transcript and CRM card.</p>}
  {qa?<p className="meta">You can still ask Coach questions when automatic tips are off.</p>:null}
  {qa?<CoachQaSection callId={callId} qa={qa} />:null}
 </section>;
}

/** One preference behind the dock switch and the in-call checkbox: stored per tenant/user, every mounted control told
 * of a change, read fresh when the user changes under a mounted control. */
export function useCoachPreference(user:string):[boolean,(on:boolean)=>void]{
 const [preference,setPreference]=useState(()=>({user,enabled:coachDefaultEnabled(coachStorage(),user)}));
 useEffect(()=>subscribeCoachPreference((forUser,on)=>{if(forUser===user)setPreference({user,enabled:on});}),[user]);
 // The call window keeps its own copy of the switch: a change in either window reaches the other through storage.
 useEffect(()=>{
  if(typeof window==='undefined')return;
  const onStorage=(e:StorageEvent)=>{if(e.key===COACH_STORAGE_KEY||e.key===null)setPreference({user,enabled:coachDefaultEnabled(coachStorage(),user)});};
  window.addEventListener('storage',onStorage);
  return()=>window.removeEventListener('storage',onStorage);
 },[user]);
 const enabled=preference.user===user?preference.enabled:coachDefaultEnabled(coachStorage(),user);
 const update=useCallback((on:boolean)=>{setPreference({user,enabled:on});saveCoachPreference(coachStorage(),user,on);},[user]);
 return [enabled,update];
}

export function LiveCoachSwitchView({enabled,copy,onToggle}:{enabled:boolean;copy:CoachSwitchCopy;onToggle:(on:boolean)=>void}){
 return <div className="coach-switch">
  <CoachStyles />
  <button type="button" role="switch" aria-checked={copy.available&&enabled} disabled={!copy.available} className="coach-switch-btn" aria-describedby="coach-switch-detail" onClick={()=>onToggle(!enabled)}>
   <Icon icon={Sparkles} /> Live coach
   <span className={`chip chip-lamp tone-${copy.tone}`}><span className={`dot dot-${copy.tone}`} aria-hidden="true" />{copy.state}</span>
  </button>
  <p className="field-hint" id="coach-switch-detail">{copy.detail}</p>
 </div>;
}

/** The phone dock's standing Live coach switch: there with or without a call, so a rep turns coaching on before
 * dialing and sees why it is unavailable instead of a panel that silently never appears (2026-10-29, Allen). */
export function LiveCoachSwitch(){
 const ws=useWorkspace(),cap=ws.config.value?.capabilities['transcript.coach'];
 const user=coachUserKey(ws.session.tenant?.id,ws.userId);
 const [enabled,setEnabled]=useCoachPreference(user);
 return <LiveCoachSwitchView enabled={enabled} copy={coachSwitchCopy(cap,ws.permissions,enabled,!!ws.config.value)} onToggle={setEnabled} />;
}

export function CoachPanel({call}:{call:Call}){
 const ws=useWorkspace(),cap=ws.config.value?.capabilities['transcript.coach'];
 const visible=call.state==='active'&&coachVisible(cap,ws.permissions,ws.mediaAccess.state==='ready');
 const user=coachUserKey(ws.session.tenant?.id,ws.userId);
 const [enabled,setEnabled]=useCoachPreference(user);
 const poller=useMemo(()=>new CoachPoller(ws.api,ws.mediaAccessStore),[ws.api,ws.mediaAccessStore,user,call.id]);
 const state=useSyncExternalStore(poller.subscribe,poller.snapshot,poller.snapshot);
 const client=useMemo(()=>new CoachAskClient(ws.api),[ws.api]);
 const scope=`${call.id}:${ws.mediaAccess.epoch}:${user}`;
 const [history,setHistory]=useState<{scope:string;entries:CoachQaEntry[]}>({scope,entries:[]});
 const entries=history.scope===scope?history.entries:[];
 const [draft,setDraft]=useState('');
 const scopeRef=useRef(scope);
 scopeRef.current=scope;
 const seq=useRef(0);
 const inFlight=useRef(new Map<string,AbortController>());

 const dispatch=useCallback((forScope:string,action:Parameters<typeof coachQaReducer>[1])=>{
  setHistory(h=>{
   const base=h.scope===forScope?h.entries:[];
   if(h.scope!==forScope&&action.type!=='asked')return h;
   return {scope:forScope,entries:coachQaReducer(base,action)};
  });
 },[]);

 useEffect(()=>()=>{
  for(const c of inFlight.current.values())c.abort();
  inFlight.current.clear();
 },[scope]);

 const runQuestion=useCallback((id:string,question:string,forScope:string,callId:string)=>{
  const controller=new AbortController();
  inFlight.current.set(id,controller);
  client.ask(callId,question,controller.signal).then(
   body=>{
    if(scopeRef.current!==forScope||controller.signal.aborted)return;
    const answer=readCoachAskResponse(body);
    if(answer)dispatch(forScope,{type:'answered',id,answer});
    else dispatch(forScope,{type:'failed',id,error:UNREADABLE_ANSWER});
   },
   error=>{
    if(scopeRef.current!==forScope||controller.signal.aborted||isAbortError(error))return;
    dispatch(forScope,{type:'failed',id,error:coachAskErrorView(error)});
   },
  ).finally(()=>{
   if(inFlight.current.get(id)===controller)inFlight.current.delete(id);
  });
 },[client,dispatch]);

 const busy=entries.some(e=>e.status==='loading');
 const onAsk=(text:string)=>{
  const question=cleanQuestion(text);
  if(!question||busy)return;
  const id=`coach-qa-${++seq.current}`;
  dispatch(scope,{type:'asked',id,question});
  setDraft('');
  runQuestion(id,question,scope,call.id);
 };
 const onRetry=(id:string)=>{
  const entry=entries.find(e=>e.id===id);
  if(!entry||busy)return;
  dispatch(scope,{type:'retried',id});
  runQuestion(id,entry.question,scope,call.id);
 };

 useEffect(()=>{
  poller.start(call,visible&&enabled);
  return ()=>poller.stop();
 },[poller,call.id,call.state,visible,enabled,ws.mediaAccess.epoch,user]);
 if(!visible)return null;
 return <CoachPanelView
  callId={call.id}
  enabled={enabled}
  onToggle={setEnabled}
  state={state}
  qa={{entries,draft,onDraft:setDraft,onAsk,onClear:()=>setDraft(''),onRetry}}
 />;
}
